import {
  Call,
  CallSearchRequest,
  PagedResult,
  KPIData,
  CallDetails,
  ReviewComment,
  QACriteria,
  CallSearchResponse,
  FilterValues,
  MassUpdateRequest,
  MassUpdateResponse,
  MassDeleteRequest,
  MassDeleteResponse,
  SimilarCallsResponse,
  SemanticSearchRequest,
  SemanticSearchResponse,
  CallAttachment,
  CadInboxItem,
  CadInboxSummary,
  CadRecord,
  CadVerification,
  CadApplyField,
  CadApplyResult,
} from '../types/Call';
import { DetailedScoresCleanupResult } from '../types/DetailedScoresCleanup';
import { apiClient } from './sessionAwareApiClient';
import { ApiError } from './apiClient';
import { notificationService } from './notificationService';

export class CallService {
  static async searchCalls(request: CallSearchRequest): Promise<PagedResult<Call>> {
    try {
      const params = new URLSearchParams();

      if (request.searchTerm) params.append('searchTerm', request.searchTerm);
      if (request.startDate) params.append('startDate', request.startDate);
      if (request.endDate) params.append('endDate', request.endDate);
      if (request.callType) params.append('callType', request.callType);
      if (request.status) params.append('status', request.status);
      if (request.reviewStatus) params.append('reviewStatus', request.reviewStatus);
      if (request.operator) params.append('operator', request.operator);
      if (request.cadReport) params.append('cadReport', request.cadReport);
      if (request.minQualityScore !== undefined)
        params.append('minQualityScore', request.minQualityScore.toString());
      if (request.maxQualityScore !== undefined)
        params.append('maxQualityScore', request.maxQualityScore.toString());
      params.append('page', request.page.toString());
      params.append('pageSize', request.pageSize.toString());
      if (request.sortBy) params.append('sortBy', request.sortBy);
      params.append('sortDescending', request.sortDescending.toString());

      const url = `/api/calls?${params}`;
      const response = await apiClient.get<PagedResult<Call>>(url);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Search Calls');
      throw _error;
    }
  }

  static async searchCallsWithDiff(
    request: CallSearchRequest,
    lastSync?: string
  ): Promise<CallSearchResponse> {
    try {
      const params = new URLSearchParams();

      if (request.searchTerm) params.append('searchTerm', request.searchTerm);
      if (request.startDate) params.append('startDate', request.startDate);
      if (request.endDate) params.append('endDate', request.endDate);
      if (request.callType) params.append('callType', request.callType);
      if (request.status) params.append('status', request.status);
      if (request.reviewStatus) params.append('reviewStatus', request.reviewStatus);
      if (request.operator) params.append('operator', request.operator);
      if (request.minQualityScore !== undefined)
        params.append('minQualityScore', request.minQualityScore.toString());
      if (request.maxQualityScore !== undefined)
        params.append('maxQualityScore', request.maxQualityScore.toString());
      params.append('page', request.page.toString());
      params.append('pageSize', request.pageSize.toString());
      if (request.sortBy) params.append('sortBy', request.sortBy);
      params.append('sortDescending', request.sortDescending.toString());
      if (lastSync) params.append('lastSync', lastSync);

      const response = await apiClient.get<CallSearchResponse>(`/api/calls?${params}`);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Search Calls');
      throw _error;
    }
  }

  static async getFilterValues(): Promise<FilterValues> {
    try {
      const response = await apiClient.get<FilterValues>('/api/calls/filter-values');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Filter Values');
      throw _error;
    }
  }

  static async getKPIData(filters?: Partial<CallSearchRequest>): Promise<KPIData> {
    try {
      const params = new URLSearchParams();

      if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.callType) params.append('callType', filters.callType);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.reviewStatus) params.append('reviewStatus', filters.reviewStatus);
      if (filters?.operator) params.append('operator', filters.operator);
      if (filters?.cadReport) params.append('cadReport', filters.cadReport);
      if (filters?.minQualityScore !== undefined)
        params.append('minQualityScore', filters.minQualityScore.toString());
      if (filters?.maxQualityScore !== undefined)
        params.append('maxQualityScore', filters.maxQualityScore.toString());

      const response = await apiClient.get<KPIData>(`/api/calls/kpi?${params}`);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Load KPI Data');

      // Return fallback data instead of throwing
      return {
        totalFiles: 0,
        averageScore: 0,
        completionPercentage: 0,
        autoAuditedPercentage: 0,
      };
    }
  }

  static async exportCalls(request: CallSearchRequest, format: 'csv' | 'excel'): Promise<Blob> {
    try {
      const params = new URLSearchParams();

      if (request.searchTerm) params.append('searchTerm', request.searchTerm);
      if (request.startDate) params.append('startDate', request.startDate);
      if (request.endDate) params.append('endDate', request.endDate);
      if (request.callType) params.append('callType', request.callType);
      if (request.status) params.append('status', request.status);
      if (request.reviewStatus) params.append('reviewStatus', request.reviewStatus);
      if (request.minQualityScore !== undefined)
        params.append('minQualityScore', request.minQualityScore.toString());
      if (request.maxQualityScore !== undefined)
        params.append('maxQualityScore', request.maxQualityScore.toString());
      if (request.sortBy) params.append('sortBy', request.sortBy);
      params.append('sortDescending', request.sortDescending.toString());
      params.append('format', format);

      const response = await apiClient.get(`/api/calls/export?${params}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Export Calls');
      throw _error;
    }
  }

  static async getCallDetails(callId: string): Promise<CallDetails> {
    try {
      const response = await apiClient.get<CallDetails>(`/api/calls/${callId}`);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Load Call Details');
      throw _error;
    }
  }

  static async getReviewComments(callId: string): Promise<ReviewComment[]> {
    try {
      const response = await apiClient.get<ReviewComment[]>(`/api/calls/${callId}/comments`);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Load Review Comments');
      throw _error;
    }
  }

  static async addReviewComment(callId: string, comment: string): Promise<ReviewComment> {
    try {
      const response = await apiClient.post<ReviewComment>(`/api/calls/${callId}/comments`, {
        comment,
      });
      notificationService.success(
        'Comment Added',
        'Your review comment has been saved successfully.'
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Add Review Comment');
      throw _error;
    }
  }

  static async getQACriteria(): Promise<QACriteria[]> {
    // Return empty array since we no longer use database-driven criteria
    // The new system uses a single prompt with pipe-delimited parsing
    return [];
  }

  static async uploadFiles(
    files: File[],
    onProgress?: (progress: number) => void
  ): Promise<string[]> {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const response = await apiClient.post<{ callIds: string[] }>('/api/calls/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      notificationService.success(
        'Upload Successful',
        `${files.length} file(s) uploaded successfully and are being processed.`
      );

      return response.data.callIds;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'File Upload');
      throw _error;
    }
  }

  static async retryProcessing(callId: string): Promise<void> {
    try {
      await apiClient.post(`/api/calls/${callId}/retry`);
      notificationService.success(
        'Processing Restarted',
        'The call processing has been restarted.'
      );
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Retry Processing');
      throw _error;
    }
  }

  static async getFullTranscription(callId: string): Promise<string> {
    try {
      const response = await apiClient.get<{ transcriptionText: string }>(
        `/api/calls/${callId}/transcription`
      );
      return response.data.transcriptionText;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Load Full Transcription');
      throw _error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async updateEnhancedScores(
    callId: string,
    enhancedScores: any,
    reviewNotes: string
  ): Promise<CallDetails> {
    try {
      const requestBody = {
        enhancedScores,
        reviewNotes,
      };

      const response = await apiClient.put<CallDetails>(
        `/api/calls/${callId}/enhanced-scores`,
        requestBody
      );
      notificationService.success('Scores Updated', 'QA scores have been updated successfully.');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Update Scores');
      throw _error;
    }
  }

  static async checkForUpdates(lastSync?: string): Promise<boolean> {
    try {
      const params = new URLSearchParams();
      if (lastSync) params.append('lastSync', lastSync);

      const response = await apiClient.get<{ hasUpdates: boolean }>(
        `/api/calls/check-updates?${params}`
      );
      return response.data.hasUpdates;
    } catch (_error) {
      // Return true on error to trigger a refresh and potentially recover
      return true;
    }
  }

  static async updateReviewStatus(callId: string, reviewStatus: string): Promise<CallDetails> {
    const requestBody = {
      reviewStatus,
      reviewedBy: 'current-user', // TODO: Get from auth context
    };

    const response = await apiClient.put<CallDetails>(
      `/api/calls/${callId}/review-status`,
      requestBody
    );
    return response.data;
  }

  static async massUpdateCalls(request: MassUpdateRequest): Promise<MassUpdateResponse> {
    try {
      const response = await apiClient.post<MassUpdateResponse>('/api/calls/mass-update', request);
      notificationService.success(
        'Mass Update Complete',
        `${response.data.updatedCount} call(s) updated successfully.`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Mass Update');
      throw _error;
    }
  }

  static async massDeleteCalls(request: MassDeleteRequest): Promise<MassDeleteResponse> {
    try {
      const response = await apiClient.post<MassDeleteResponse>('/api/calls/mass-delete', request);
      notificationService.success(
        'Calls Deleted',
        `${response.data.deletedCount} call(s) permanently deleted.`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Mass Delete');
      throw _error;
    }
  }

  /**
   * The effective minimum retention period before a call may be deleted.
   *
   * The resolved floor — the longer of the platform default and the agency's own setting — not the
   * raw configuration key, which is usually unset on the tenant while the platform floor applies.
   * Zero means no floor. Deliberately not surfaced through `notificationService` on failure: the
   * caller treats an unknown floor as "no local restriction" and lets the API refuse, which is the
   * behaviour that existed before this endpoint.
   */
  static async getRetentionPolicy(): Promise<number> {
    const response = await apiClient.get<{ minRetentionDays: number }>(
      '/api/calls/retention-policy'
    );
    return response.data.minRetentionDays;
  }

  static async regenerateSummary(callId: string): Promise<CallDetails> {
    try {
      const response = await apiClient.post<CallDetails>(`/api/calls/${callId}/regenerate-summary`);
      notificationService.success('Summary Generated', 'AI summary has been generated successfully.');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Generate Summary');
      throw _error;
    }
  }

  /**
   * Queue AI analysis regeneration for a call. Returns immediately (202 Accepted) —
   * the analysis runs in the background. The caller is responsible for polling
   * getCallDetails until status transitions to 'completed' or 'ai_failed'.
   */
  static async regenerateAnalysis(callId: string): Promise<void> {
    try {
      await apiClient.post(`/api/calls/${callId}/regenerate-analysis`);
      notificationService.info('Analysis Queued', 'AI analysis is running in the background. This page will update automatically.');
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Generate Analysis');
      throw _error;
    }
  }

  static async findSimilarCalls(callId: string, maxResults = 10): Promise<SimilarCallsResponse> {
    try {
      const response = await apiClient.get<SimilarCallsResponse>(
        `/api/calls/${callId}/similar?maxResults=${maxResults}`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Find Similar Calls');
      throw _error;
    }
  }

  static async semanticSearch(request: SemanticSearchRequest): Promise<SemanticSearchResponse> {
    try {
      const response = await apiClient.post<SemanticSearchResponse>('/api/calls/search', request);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Smart Search');
      throw _error;
    }
  }

  /**
   * The call's CAD record attachments, or null when the agency has CAD records turned off.
   *
   * The API answers every CAD route with 404 while the tenant flag is off, for every role, so this
   * list doubles as the feature check. 404 is accepted as a response rather than raised, so an
   * agency without the feature gets no error toast or error log on every call page. The list is
   * metadata only and unaudited, which also makes it the endpoint to poll during extraction.
   */
  static async listCadRecords(callId: string): Promise<CallAttachment[] | null> {
    const response = await apiClient.get<CallAttachment[]>(`/api/calls/${callId}/cad`, {
      validateStatus: (status) => status === 200 || status === 404,
    });
    return response.status === 404 ? null : response.data;
  }

  /** Attaches a CAD event report PDF, replacing any existing one, and queues extraction. */
  static async attachCadRecord(
    callId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<CallAttachment> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<CallAttachment>(`/api/calls/${callId}/cad`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      notificationService.success('CAD Record Attached', 'The CAD report is being read.');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Attach CAD Record');
      throw _error;
    }
  }

  /**
   * CAD reports waiting for a call, the ones that name this call first. Null when the agency has CAD
   * records off, or the user may not attach (an Agent): the picker simply does not render.
   */
  static async listCadInbox(callId: string): Promise<CadInboxItem[] | null> {
    const response = await apiClient.get<CadInboxItem[]>(`/api/calls/${callId}/cad/inbox`, {
      validateStatus: (status) => status === 200 || status === 403 || status === 404,
    });
    return response.status === 200 ? response.data : null;
  }

  /** Attaches a waiting report to this call and takes it off the shelf. */
  static async attachCadFromInbox(callId: string, inboxItemId: string): Promise<CallAttachment> {
    try {
      const response = await apiClient.post<CallAttachment>(
        `/api/calls/${callId}/cad/inbox/${inboxItemId}/attach`
      );
      notificationService.success('CAD Record Attached', 'The CAD report is being read.');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Attach CAD Record');
      throw _error;
    }
  }

  /** Uploads one CAD report PDF to the shelf; it is matched to a call within a few minutes. */
  static async uploadCadInboxFile(file: File): Promise<CadInboxItem> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<CadInboxItem>('/api/cad-inbox', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /** How many reports are waiting and whether PDFs are accepted in bulk; null when CAD is off or the user may not upload. */
  static async getCadInboxSummary(): Promise<CadInboxSummary | null> {
    const response = await apiClient.get<CadInboxSummary>('/api/cad-inbox/summary', {
      validateStatus: (status) => status === 200 || status === 403 || status === 404,
    });
    return response.status === 200 ? response.data : null;
  }

  /** Deletes a waiting report. */
  static async deleteCadInboxItem(inboxItemId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/cad-inbox/${inboxItemId}`);
      notificationService.success('CAD Report Deleted', 'The waiting report was removed.');
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Delete CAD Report');
      throw _error;
    }
  }

  /** The fields extracted from the report. Each call writes a durable VIEW_CAD_RECORD audit record. */
  static async getCadRecordFields(callId: string, attachmentId: string): Promise<CadRecord> {
    try {
      const response = await apiClient.get<CadRecord>(
        `/api/calls/${callId}/cad/${attachmentId}/fields`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Load CAD Record Fields');
      throw _error;
    }
  }

  /** The PDF itself. Each call writes a durable VIEW_CAD_RECORD audit record. */
  static async getCadRecordContent(callId: string, attachmentId: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`/api/calls/${callId}/cad/${attachmentId}/content`, {
        responseType: 'blob',
        timeout: 60000,
      });
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Open CAD Record');
      throw _error;
    }
  }

  /** Queues the report to be read again. */
  static async requestCadExtraction(callId: string, attachmentId: string): Promise<CallAttachment> {
    try {
      const response = await apiClient.post<CallAttachment>(
        `/api/calls/${callId}/cad/${attachmentId}/extract`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Retry CAD Extraction');
      throw _error;
    }
  }

  /** Queues the CAD record to be checked against the transcript. The row comes back as Pending. */
  static async requestCadVerification(callId: string, attachmentId: string): Promise<CallAttachment> {
    try {
      const response = await apiClient.post<CallAttachment>(
        `/api/calls/${callId}/cad/${attachmentId}/verify`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Check CAD Record');
      throw _error;
    }
  }

  /** Queues the CAD Skills rescore against the record (202 with the row); the caller polls the list. */
  static async requestCadRescore(callId: string, attachmentId: string): Promise<CallAttachment> {
    try {
      const response = await apiClient.post<CallAttachment>(
        `/api/calls/${callId}/cad/${attachmentId}/rescore`
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Update CAD Skills Score');
      throw _error;
    }
  }

  /**
   * The check's status, and its result once Verified. A status-only answer writes no audit record,
   * so this is safe to poll; an answer carrying the result is a durable VIEW_CAD_RECORD read.
   * No notification here: the caller polls it, and the tab shows its own state.
   */
  static async getCadVerification(callId: string, attachmentId: string): Promise<CadVerification> {
    const response = await apiClient.get<CadVerification>(
      `/api/calls/${callId}/cad/${attachmentId}/verification`
    );
    return response.data;
  }

  /** Writes the selected call record fields from the CAD record, with previous values in the audit trail. */
  static async applyCadRecord(
    callId: string,
    attachmentId: string,
    fields: CadApplyField[]
  ): Promise<CadApplyResult> {
    try {
      const response = await apiClient.post<CadApplyResult>(
        `/api/calls/${callId}/cad/${attachmentId}/apply`,
        { fields }
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Apply CAD Record');
      throw _error;
    }
  }

  /**
   * Re-reads the call after a change that can move it out of the reader's scope (applying an
   * operator). 403 and 404 come back as null rather than an error toast, so the page can say what
   * actually happened instead of "access denied".
   */
  static async getCallDetailsIfVisible(callId: string): Promise<CallDetails | null> {
    const response = await apiClient.get<CallDetails>(`/api/calls/${callId}`, {
      validateStatus: (status) => status === 200 || status === 403 || status === 404,
    });
    return response.status === 200 ? response.data : null;
  }

  /**
   * Purge corrupted (unparseable) category keys from stored call scores. TenantAdmin only.
   * dryRun=true reports the affected calls without writing; dryRun=false applies the fix and
   * recomputes each call's overall score.
   */
  static async cleanupDetailedScores(dryRun: boolean): Promise<DetailedScoresCleanupResult> {
    try {
      const response = await apiClient.post<DetailedScoresCleanupResult>(
        '/api/calls/detailed-scores/cleanup',
        { dryRun }
      );
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Clean Up Category Data');
      throw _error;
    }
  }
}
